/**
 * Utility for playing sound effects in the application
 */

import { logger } from '@/models/logger';

// Cache for preloaded audio objects
const audioCache: { [key: string]: HTMLAudioElement } = {};

// Sound URLs
const SOUND_URLS = {
  NEW_CHAT_MESSAGE: 'https://cdn.freesound.org/previews/716/716447_4077593-lq.mp3',
  // Add more sounds here as needed
};

/**
 * Preload sounds for faster playback
 */
export const preloadSounds = (): void => {
  Object.entries(SOUND_URLS).forEach(([key, url]) => {
    if (!audioCache[key]) {
      const audio = new Audio(url);
      audio.load();
      audioCache[key] = audio;
    }
  });
};

/**
 * Play a sound by its key
 * @param soundKey - Key of the sound to play
 * @param volume - Volume level (0.0 to 1.0)
 * @returns Promise that resolves when the sound starts playing
 */
export const playSound = async (soundKey: keyof typeof SOUND_URLS, volume = 1.0): Promise<void> => {
  try {
    const url = SOUND_URLS[soundKey];
    
    // Get audio from cache or create a new one
    let audio = audioCache[soundKey];
    if (!audio) {
      audio = new Audio(url);
      audioCache[soundKey] = audio;
    }
    
    // Reset audio to start
    audio.currentTime = 0;
    audio.volume = Math.max(0, Math.min(1, volume));
    
    // Play the sound
    await audio.play();
  } catch (error) {
    logger.error(`Failed to play sound: ${soundKey}`, error);
  }
};

/**
 * Play new chat message notification sound
 * @param volume - Volume level (0.0 to 1.0)
 */
export const playNewChatMessage = (volume = 1.0): Promise<void> => {
  logger.debug('🔊 playNewChatMessage triggered with volume', volume)
  return playSound('NEW_CHAT_MESSAGE', volume);
};

// Initialize by preloading sounds when module is imported
if (typeof window !== 'undefined') {
  // Only run in browser environment
  preloadSounds();
} 